/**
 * 피벗 필드 패널 (AG 피벗 패널 / DevExtreme 필드 선택기) — 필드를 행·열·값 영역으로 끌어 놓고,
 * 값마다 집계를 고르면 onChange(spec) 로 바로 다시 피벗한다.
 *
 *   const pp = new PivotPanel(el, { fields: [{ id: 'dept', label: '진료과' }, { id: 'rslt', label: '결과', numeric: true }],
 *     spec: { rows: ['dept'], cols: [], values: [] }, onChange: s => show(pivot(ds, grid.getViewRows(), s)) })
 */
import { injectCss } from './css.js';
export const PIVOT_AGG_LABEL = { sum: '합계', avg: '평균', count: '건수', min: '최소', max: '최대' };
/** 순수: 필드를 영역으로 옮긴 새 spec. 행·열은 서로 배타, 값은 같은 필드를 다른 집계로 여러 번 둘 수 있음 */
export function movePivotField(spec, id, to, index, from, numeric = false) {
    const s = { rows: [...spec.rows], cols: [...spec.cols], values: spec.values.map(v => ({ ...v })) };
    let moved;
    if (from?.area === 'values')
        moved = s.values.splice(from.index, 1)[0];
    if (to !== 'values' || from?.area === 'rows' || from?.area === 'cols') {
        s.rows = s.rows.filter(x => x !== id);
        s.cols = s.cols.filter(x => x !== id);
    }
    const at = (n) => (index == null || index < 0 || index > n ? n : index);
    if (to === 'rows')
        s.rows.splice(at(s.rows.length), 0, id);
    else if (to === 'cols')
        s.cols.splice(at(s.cols.length), 0, id);
    else if (to === 'values')
        s.values.splice(at(s.values.length), 0, moved ?? { col: id, agg: numeric ? 'sum' : 'count' });
    return s;
}
export class PivotPanel {
    constructor(host, opts) {
        this.opts = opts;
        this.drag = null;
        injectCss();
        this.spec = { rows: [...(opts.spec?.rows ?? [])], cols: [...(opts.spec?.cols ?? [])], values: (opts.spec?.values ?? []).map(v => ({ ...v })) };
        this.el = document.createElement('div');
        this.el.className = 'og-pp';
        this.el.setAttribute('role', 'region');
        this.el.setAttribute('aria-label', '피벗 필드');
        host.appendChild(this.el);
        this.render();
    }
    getSpec() { return { rows: [...this.spec.rows], cols: [...this.spec.cols], values: this.spec.values.map(v => ({ ...v })) }; }
    setSpec(spec, emit = false) {
        this.spec = { rows: [...(spec.rows ?? [])], cols: [...(spec.cols ?? [])], values: (spec.values ?? []).map(v => ({ ...v })) };
        this.render();
        if (emit)
            this.emit();
    }
    /** 코드·키보드로 옮기기 (끌기와 같은 결과) */
    moveField(id, to, index, from) {
        const f = this.opts.fields.find(x => x.id === id);
        if (!f)
            return;
        this.spec = movePivotField(this.spec, id, to, index, from, !!f.numeric);
        this.render();
        this.emit();
    }
    setAgg(valueIndex, agg) {
        const v = this.spec.values[valueIndex];
        if (!v || v.agg === agg)
            return;
        v.agg = agg;
        delete v.label;
        this.render();
        this.emit();
    }
    destroy() { this.el.remove(); }
    emit() { this.opts.onChange?.(this.getSpec()); }
    label(id) { return this.opts.fields.find(f => f.id === id)?.label ?? id; }
    render() {
        const tr = this.opts.translate ?? ((k) => k);
        const T = { fields: tr('필드'), rows: tr('행'), cols: tr('열'), values: tr('값'), ...this.opts.titles };
        this.el.textContent = '';
        const used = new Set([...this.spec.rows, ...this.spec.cols]);
        const areas = [
            ['fields', this.opts.fields.filter(f => !used.has(f.id)).map(f => f.id)],
            ['rows', this.spec.rows], ['cols', this.spec.cols], ['values', this.spec.values.map(v => v.col)],
        ];
        for (const [area, ids] of areas) {
            const box = document.createElement('div');
            box.className = 'og-pp-area';
            box.dataset.area = area;
            const h = document.createElement('div');
            h.className = 'og-pp-h';
            h.textContent = T[area];
            box.appendChild(h);
            const list = document.createElement('div');
            list.className = 'og-pp-list';
            box.appendChild(list);
            ids.forEach((id, i) => list.appendChild(this.chip(area, id, i)));
            if (!ids.length && area !== 'fields') {
                const e = document.createElement('div');
                e.className = 'og-pp-empty';
                e.textContent = (this.opts.translate ?? (k => k))('여기로 끌어 놓기');
                list.appendChild(e);
            }
            box.addEventListener('dragover', e => { if (!this.drag)
                return; e.preventDefault(); box.classList.add('og-pp-over'); });
            box.addEventListener('dragleave', () => box.classList.remove('og-pp-over'));
            box.addEventListener('drop', e => {
                e.preventDefault();
                box.classList.remove('og-pp-over');
                const d = this.drag;
                this.drag = null;
                if (!d)
                    return;
                // 놓은 위치: 그 칩 앞
                const chips = [...list.querySelectorAll('.og-pp-chip')];
                let idx = chips.findIndex(c => { const b = c.getBoundingClientRect(); return e.clientY < b.top + b.height / 2; });
                if (idx < 0)
                    idx = chips.length;
                if (area === 'fields') {
                    if (d.area === 'values')
                        this.removeValue(d.index);
                    else if (d.area !== 'fields')
                        this.removeDim(d.id);
                    return;
                }
                if (d.area === area && area !== 'values' && idx > d.index)
                    idx--;
                if (d.area === area && area === 'values' && idx > d.index)
                    idx--;
                this.moveField(d.id, area, idx, { area: d.area, index: d.index });
            });
            this.el.appendChild(box);
        }
    }
    removeDim(id) { this.spec.rows = this.spec.rows.filter(x => x !== id); this.spec.cols = this.spec.cols.filter(x => x !== id); this.render(); this.emit(); }
    removeValue(i) { this.spec.values.splice(i, 1); this.render(); this.emit(); }
    chip(area, id, index) {
        const c = document.createElement('div');
        c.className = 'og-pp-chip';
        c.draggable = true;
        c.dataset.field = id;
        c.dataset.index = String(index);
        const t = document.createElement('span');
        t.textContent = this.label(id);
        c.appendChild(t);
        if (area === 'values') {
            const sel = document.createElement('select');
            sel.className = 'og-pp-agg';
            sel.setAttribute('aria-label', `${this.label(id)} 집계`);
            const numeric = !!this.opts.fields.find(f => f.id === id)?.numeric;
            for (const a of (numeric ? ['sum', 'avg', 'count', 'min', 'max'] : ['count'])) {
                const o = document.createElement('option');
                o.value = a;
                o.textContent = (this.opts.translate ?? (k => k))(PIVOT_AGG_LABEL[a]);
                sel.appendChild(o);
            }
            sel.value = this.spec.values[index].agg;
            sel.addEventListener('change', () => this.setAgg(index, sel.value));
            sel.addEventListener('mousedown', e => e.stopPropagation());
            c.appendChild(sel);
        }
        if (area !== 'fields') {
            const x = document.createElement('button');
            x.type = 'button';
            x.className = 'og-pp-x';
            x.textContent = '×';
            x.setAttribute('aria-label', `${this.label(id)} 빼기`);
            x.addEventListener('click', () => area === 'values' ? this.removeValue(index) : this.removeDim(id));
            c.appendChild(x);
        }
        c.addEventListener('dragstart', e => { this.drag = { id, area, index }; e.dataTransfer?.setData('text/plain', id); if (e.dataTransfer)
            e.dataTransfer.effectAllowed = 'move'; });
        c.addEventListener('dragend', () => { this.drag = null; });
        return c;
    }
}
/** 패널 spec → pivot() spec (값이 없으면 건수 하나) */
export function pivotSpecOf(s, labels, countCol) {
    const values = s.values.length ? s.values : [{ col: countCol ?? s.rows[0] ?? s.cols[0] ?? '', agg: 'count' }];
    return { rows: s.rows, cols: s.cols, values, labels };
}
